import React, { useEffect, useRef, useCallback } from 'react';
import { a11y } from '../utils/responsive';

/**
 * Hook for managing focus within a component
 */
export const useFocusManagement = (isOpen = true) => {
  const containerRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      // Store the previously focused element
      previousFocusRef.current = document.activeElement;
      
      // Focus the first focusable element in the container
      const focusableElements = containerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }

      // Set up focus trap
      const cleanup = a11y.trapFocus(containerRef.current);
      
      return () => {
        cleanup();
        // Restore focus to the previously focused element
        if (previousFocusRef.current && previousFocusRef.current.focus) {
          previousFocusRef.current.focus();
        }
      };
    }
  }, [isOpen]);

  return containerRef;
};

/**
 * Hook for managing keyboard navigation
 */
export const useKeyboardNavigation = (handlers = {}) => {
  const {
    onEscape,
    onEnter,
    onArrowUp,
    onArrowDown,
    onArrowLeft,
    onArrowRight,
    onTab,
    onHome,
    onEnd
  } = handlers;

  const handleKeyDown = useCallback((event) => {
    switch (event.key) {
      case 'Escape':
        onEscape?.(event);
        break;
      case 'Enter':
        onEnter?.(event);
        break;
      case 'ArrowUp':
        onArrowUp?.(event);
        break;
      case 'ArrowDown':
        onArrowDown?.(event);
        break;
      case 'ArrowLeft':
        onArrowLeft?.(event);
        break;
      case 'ArrowRight':
        onArrowRight?.(event);
        break;
      case 'Tab':
        onTab?.(event);
        break;
      case 'Home':
        onHome?.(event);
        break;
      case 'End':
        onEnd?.(event);
        break;
      default:
        break;
    }
  }, [onEscape, onEnter, onArrowUp, onArrowDown, onArrowLeft, onArrowRight, onTab, onHome, onEnd]);

  return { onKeyDown: handleKeyDown };
};

/**
 * Hook for screen reader announcements
 */
export const useScreenReader = () => {
  const announce = useCallback((message, priority = 'polite') => {
    a11y.announce(message, priority);
  }, []);

  const announceError = useCallback((message) => {
    announce(message, 'assertive');
  }, [announce]);

  const announceSuccess = useCallback((message) => {
    announce(message, 'polite');
  }, [announce]);

  return { announce, announceError, announceSuccess };
};

/**
 * Hook for managing ARIA attributes
 */
export const useAriaAttributes = (initialState = {}) => {
  const ariaRef = useRef(initialState);

  const updateAria = useCallback((updates) => {
    ariaRef.current = { ...ariaRef.current, ...updates };
  }, []);

  const getAriaProps = useCallback(() => {
    return Object.entries(ariaRef.current).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[`aria-${key}`] = value;
      }
      return acc;
    }, {});
  }, []);

  return { updateAria, getAriaProps };
};

/**
 * Hook for managing reduced motion preferences
 */
export const useReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (event) => setPrefersReducedMotion(event.matches);
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersReducedMotion;
};

/**
 * Hook for managing color scheme preferences
 */
export const useColorScheme = () => {
  const [prefersDark, setPrefersDark] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event) => setPrefersDark(event.matches);
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersDark;
};

/**
 * Hook for managing high contrast preferences
 */
export const useHighContrast = () => {
  const [prefersHighContrast, setPrefersHighContrast] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-contrast: high)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    const handler = (event) => setPrefersHighContrast(event.matches);
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersHighContrast;
};

/**
 * Hook for managing skip links
 */
export const useSkipLinks = (links = []) => {
  const skipLinksRef = useRef(null);

  useEffect(() => {
    if (skipLinksRef.current && links.length > 0) {
      const handleFocus = (event) => {
        if (event.target.matches('a[href^="#"]')) {
          const targetId = event.target.getAttribute('href').substring(1);
          const targetElement = document.getElementById(targetId);
          
          if (targetElement) {
            // Ensure the target is focusable
            if (!targetElement.hasAttribute('tabindex')) {
              targetElement.setAttribute('tabindex', '-1');
            }
            
            setTimeout(() => {
              targetElement.focus();
            }, 100);
          }
        }
      };

      skipLinksRef.current.addEventListener('click', handleFocus);
      
      return () => {
        if (skipLinksRef.current) {
          skipLinksRef.current.removeEventListener('click', handleFocus);
        }
      };
    }
  }, [links]);

  return skipLinksRef;
};

/**
 * Hook for managing live regions
 */
export const useLiveRegion = (initialMessage = '', priority = 'polite') => {
  const liveRegionRef = useRef(null);
  const [message, setMessage] = React.useState(initialMessage);

  useEffect(() => {
    if (liveRegionRef.current && message) {
      liveRegionRef.current.textContent = message;
      
      // Clear the message after announcement
      const timer = setTimeout(() => {
        setMessage('');
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [message]);

  const announce = useCallback((newMessage) => {
    setMessage(newMessage);
  }, []);

  return { 
    announce, 
    liveRegionRef,
    message,
    priority
  };
};

export default {
  useFocusManagement,
  useKeyboardNavigation,
  useScreenReader,
  useAriaAttributes,
  useReducedMotion,
  useColorScheme,
  useHighContrast,
  useSkipLinks,
  useLiveRegion
};